import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["color", "iconColor", "icon", "selected", "size", "opacity", "onClick", "borderColor", "borderWidth", "disabled", "tooltipProps", "data-test-id"];
import unescapedText from 'I18n/utils/unescapedText';
import { DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import { Button } from '../Button/Button';
import { FieldsetContextConsumer } from '../../context/FieldsetContext';
import { rgba } from '../../utils/color';
import { callIfPossible } from '../../utils/functions';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { IconCircle } from '../IconCircle/IconCircle';
import { uniqueId } from '../../utils/uniqueId';
import { ColorSwatchProvider } from './ColorSwatchProvider';
import { useTheme } from 'foundations-theming/utils';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { Tooltip } from '../Tooltip/Tooltip';
import { createHoverStateSelector } from '../../utils/styles/styles';
import { getFocusPseudoClass, getFocusStyleObject } from '../../utils/focus/focus.style';
import { SuccessIcon } from 'foundations-assets/icons/components/SuccessIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SIZES = {
  small: 16,
  default: 20
};
export const ColorSwatch = _ref => {
  var _theme$color;
  let {
      color,
      iconColor = 'icon-core-on-fill-default',
      icon: Icon = SuccessIcon,
      selected = false,
      size,
      opacity,
      onClick,
      borderColor,
      borderWidth = '0',
      disabled = false,
      tooltipProps,
      'data-test-id': dataTestId = 'trellis-color-swatch'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const id = uniqueId('color-input');
  const theme = useTheme();
  const colorValue = (_theme$color = theme.color[color]) !== null && _theme$color !== void 0 ? _theme$color : color;
  const handleClick = () => {
    callIfPossible(onClick, syntheticEvent(colorValue));
  };
  const hasOpacity = opacity != null;
  const backgroundColor = hasOpacity && colorValue != null ? rgba(colorValue, opacity / 100) : colorValue;
  const computedColor = selected ? iconColor : 'fill-transparent-default';
  const describedById = `${id}-desc`;
  return /*#__PURE__*/_jsx(FieldsetContextConsumer, {
    children: fieldsetContext => {
      const computedSize = size != null ? size : SIZES[fieldsetContext.size];
      return /*#__PURE__*/_jsx(Tooltip, Object.assign({
        disabled: !disabled
      }, tooltipProps, {
        [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: 'ColorSwatch',
        children: /*#__PURE__*/_jsx(ColorSwatchProvider, {
          buttonStyleOverrides: Object.assign({
            blockSize: computedSize * 2,
            inlineSize: computedSize * 2,
            borderRadius: '50%'
          }, hasOpacity ? {
            backgroundImage: `url('${require('bender-url!ui-images/optimized/canvas/transparent_background.svg')}')`
          } : undefined, !disabled && {
            [createHoverStateSelector('&:hover')]: {
              outline: `${theme.borderWidth['100']} solid ${theme.color['border-core-hover']}`,
              outlineOffset: `${theme.borderWidth['100']}`
            },
            [getFocusPseudoClass()]: Object.assign({}, getFocusStyleObject({
              theme,
              unwrapped: true
            }), {
              // sadly, we have to set this _again_ here to ensure that this takes higher
              // specificity than the default focus styles applied to the button element
              borderRadius: '50%',
              outlineOffset: `${theme.borderWidth['100']}`
            })
          }, disabled && {
            '&[aria-disabled=true]': {
              cursor: 'not-allowed'
            }
          }),
          iconCircleStyleOverrides: {
            backgroundColor,
            fontSize: computedSize
          },
          children: /*#__PURE__*/_jsxs(Button, {
            use: "unstyled",
            "aria-describedby": describedById,
            onClick: handleClick,
            "data-color-swatch": true,
            disabled: disabled,
            children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
              id: describedById,
              children: colorValue ? unescapedText('ui.UIColorSwatch.label', {
                value: colorValue
              }) : unescapedText('ui.UIColorSwatch.noColorLabel')
            }), /*#__PURE__*/_jsx(IconCircle, Object.assign({}, rest, {
              borderColor: borderColor,
              borderWidth: borderWidth,
              color: computedColor,
              margin: "0 !important",
              legacy: true,
              "data-test-id": dataTestId,
              icon: Icon,
              size: "inherit" // Size is set by `fontSize` in `iconCircleStyleOverrides`
            })), /*#__PURE__*/_jsx(ScreenReaderOnly, {
              id: describedById,
              children: colorValue ? unescapedText('ui.UIColorSwatch.label', {
                value: colorValue
              }) : unescapedText('ui.UIColorSwatch.noColorLabel')
            })]
          })
        })
      }));
    }
  });
};
ColorSwatch.displayName = 'ColorSwatch';